import React from 'react';
import Button from '../../components/common/Button';
import CompetitionTypePill from '../../components/common/CompetitionTypePill';
import CompetitionModePill from '../../components/common/CompetitionModePill';
import CompetitionVisibilityPill from '../../components/common/CompetitionVisibilityPill';
import { Competition } from '../../types/interfaces';
import { CompetitionAction, deadlineText, participantsText } from './competitionView';
import './CompetitionPage.css';

const STATUS_TIP: Record<string, string> = {
  new: 'Status: New. Not open for submissions yet',
  open: 'Status: Open. Accepting submissions',
  closed: 'Status: Closed. No longer accepting submissions',
  archived: 'Status: Archived. Finished, results are final',
};

const VISIBILITY_TIP: Record<string, string> = {
  public: 'Public: anyone signed in can find and join it',
  private: 'Private: only invited people can see it',
  unlisted: 'Unlisted: only people with the link can see it',
  showcase: 'Showcase: results are shown publicly',
};

/** Shows a short explanation on hover, keyboard focus or tap. */
const Tip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <span className="pill-tip" data-tip={text} tabIndex={0}>
    {children}
    <span className="sr-only">{text}</span>
  </span>
);

const ACTION_LABEL: Record<Exclude<CompetitionAction, null>, string> = { join: 'Join', evaluate: 'Evaluate', manage: 'Manage' };

interface Props {
  competition: Competition;
  prizeLabel: string | null;
  action: CompetitionAction;
  actionBusy: boolean;
  onAction: () => void;
}

const CompetitionHeaderStrip: React.FC<Props> = ({ competition, prizeLabel, action, actionBusy, onAction }) => {
  const statusClass = (competition.status || 'default').toLowerCase().replace(/\s+/g, '-');
  return (
    <section className="competition-strip" data-tour="competition-header">
      <div className="competition-strip__top">
        {competition.logo_path && <img src={competition.logo_path} alt={`${competition.title} logo`} className="competition-strip__logo" />}
        <div className="competition-strip__title">
          <h1>{competition.title}</h1>
          <div className="competition-strip__pills" data-tour="competition-pills">
            <Tip text={STATUS_TIP[statusClass] || `Status: ${competition.status}`}>
              <span className={`dashboard__competition-status dashboard__competition-status--${statusClass}`}>{competition.status}</span>
            </Tip>
            <Tip text={`Submission type: ${(competition.type || 'unknown').toUpperCase()}`}>
              <CompetitionTypePill type={competition.type} />
            </Tip>
            <Tip text={competition.competition_mode === 'team' ? 'Team competition: teams submit together' : 'Individual competition: everyone submits on their own'}>
              <CompetitionModePill mode={competition.competition_mode} />
            </Tip>
            {prizeLabel && (
              <Tip text={`Prize: ${prizeLabel}`}><span className="competition-meta-prize-tag">{prizeLabel}</span></Tip>
            )}
          </div>
          <p className="competition-strip__meta">
            <Tip text={VISIBILITY_TIP[(competition.visibility || '').toLowerCase()] || 'Visibility'}>
              <CompetitionVisibilityPill visibility={competition.visibility} showLabel />
            </Tip>
            {competition.sponsored_by && <span>Sponsored by {competition.sponsored_by}</span>}
          </p>
        </div>
        {action && (
          <div className="competition-strip__action" data-tour="competition-action">
            <Button variant={action === 'manage' ? 'secondary' : 'primary'} size="lg" onClick={onAction} disabled={actionBusy} loading={actionBusy}>
              {ACTION_LABEL[action]}
            </Button>
          </div>
        )}
      </div>
      <p className="competition-strip__deadline" data-tour="competition-deadline">
        {[deadlineText(competition.end_date), participantsText(competition.participants, competition.competition_mode === 'team')]
          .filter(Boolean).join(' · ')}
      </p>
    </section>
  );
};

export default CompetitionHeaderStrip;
